import {useEffect, useState} from "react";
import {
    Alert,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
} from "@mui/material";
import Box from "@mui/material/Box";
import TextField from "@mui/material/TextField";
import {LocalizationProvider} from "@mui/x-date-pickers/LocalizationProvider";
import {AdapterDateFns} from "@mui/x-date-pickers/AdapterDateFns";
import {DatePicker} from "@mui/x-date-pickers/DatePicker";
import {format, isAfter, isBefore} from "date-fns";
import Button from "@mui/material/Button";
import {ClearScheduleDates} from "../icons/ClearScheduleDates";
import plusSign from "../../assets/plus-sign.svg";
import CheckIcon from "@mui/icons-material/Check";
import $ from "jquery";
import apiURL from "../../config/environment";

export const EditScheduleDialog = ({
  existingRows,
  rowToEdit,
  setExistingRows,
  handleClose,
}) => {
    const [deletedRanges, setDeletedRanges] = useState([])
    const [editRangeIds, setEditRangeIds] = useState(new Set());
    const [schedules, setSchedules] = useState([]);
    const [saving, setSaving] = useState(false);
    const [name, setName] = useState(rowToEdit?.name);

    const [nameError, setNameError] = useState(false);
    const [saveError, setSaveError] = useState(false);
    const [dateError, setDateError] = useState(new Map());

    useEffect(() => {
        if (rowToEdit !== null) {
            if (rowToEdit.ranges.length < 3) {
                const newRanges = [];
                for (let i = 0; i < 3; i++) {
                    if (rowToEdit.ranges[i]) {
                        newRanges.push(rowToEdit.ranges[i]);
                        continue;
                    }
                    newRanges.push({
                        id: "new_" + new Date().toISOString() + i,
                        startDate: null,
                        endDate: null,
                    });
                }
                setSchedules(newRanges);
            } else {
                setSchedules(rowToEdit.ranges);
            }
            setName(rowToEdit.name);
        }
    }, [rowToEdit])


    const handleDialogClose = () => {
        handleClose();
        setDeletedRanges([]);
        setEditRangeIds(new Set());
        setSaving(false);

        setNameError(false);
        setSaveError(false);
        setDateError(new Map());
    };

    const startDateOnChange = (schedule, nv) => {
        setSchedules((prev) => {
            return prev.map(s => s.id === schedule.id ? {...s, startDate: nv} : s);
        });
        if (typeof schedule.id === 'number') {
            setEditRangeIds((prev) => {
                if (prev.has(schedule.id)) {
                    return prev
                }
                const newSet = new Set(prev);
                newSet.add(schedule.id);
                return newSet;
            });
        }
        if (nv && schedule.endDate && isAfter(nv, new Date(schedule.endDate))) {
            setDateError((prev) => {
                const newError = new Map(prev);
                newError.set(schedule.id + "_start", "Start Date cannot be after End Date");
                return newError;
            });
            return;
        }
        setDateError((prev) => {
            if (prev.has(schedule.id + "_start")) {
                const newError = new Map(prev);
                newError.delete(schedule.id + "_start");
                return newError;
            }
            return prev;
        });

    }

    const endDateOnChange = (schedule, nv) => {
        setSchedules((prev) => {
            return prev.map(s => s.id === schedule.id ? {...s, endDate: nv} : s);
        });
        if (typeof schedule.id === 'number') {
            setEditRangeIds((prev) => {
                if (prev.has(schedule.id)) {
                    return prev
                }
                const newSet = new Set(prev);
                newSet.add(schedule.id);
                return newSet;
            });
        }

        if (nv && !schedule.startDate) {
            setDateError((prev) => {
                const newError = new Map(prev);
                newError.set(schedule.id + "_start", "Start Date is required");
                return newError;
            });
            return;
        }

        if (nv && schedule.startDate && isBefore(nv, new Date(schedule.startDate))) {
            setDateError((prev) => {
                const newError = new Map(prev);
                newError.set(schedule.id + "_end", "End Date cannot be before Start Date");
                return newError;
            });
            return;
        }
        setDateError((prev) => {
            if (prev.has(schedule.id + "_end")) {
                const newError = new Map(prev);
                newError.delete(schedule.id + "_end");
                return newError;
            }
            return prev;
        });

    }

    const handleSave = () => {
        if (saving ||
            name === "" ||
            nameError ||
            schedules.filter((s) => s.startDate !== null).length === 0
        ) {
            return;
        }

        const rangesToEdit = [...editRangeIds].map((id) => {
            const schedule = schedules.find(s => s.id === id);
            return {
                id: schedule.id,
                startDate: format(schedule.startDate, "yyyy-MM-dd"),
                endDate: schedule.endDate ? format(schedule.endDate, "yyyy-MM-dd") : null,
            };
        })

        setSaving(true);
        const token = JSON.parse(localStorage.getItem("userInfo"))?.token;
        $.ajax({
            method: "PATCH",
            url: apiURL + "admin/schedule",
            headers: {
                Authorization: `Bearer ${token}`,
            },
            contentType: "application/json",
            data: JSON.stringify({
                name: name !== rowToEdit.name ? name : null,
                deleteRanges: deletedRanges.length > 0 ? deletedRanges : null,
                editRanges: rangesToEdit.length > 0 ? rangesToEdit : null,
                addRanges: schedules.filter((s) => s.startDate !== null && typeof s.id === 'string').map((s) => ({
                    start: s.startDate ? format(s.startDate, "yyyy-MM-dd") : null,
                    end: s.endDate ? format(s.endDate, "yyyy-MM-dd") : null,
                })),
                id: rowToEdit.id,
            }),
        })
            .done((data) => {
                setSaving(false);
                setExistingRows((prev )  => {
                  const withoutEdit = prev.filter(s => s.id !== data.id);
                  return [...withoutEdit, data];
                });
                handleDialogClose();
            })
            .fail((jqXHR, textStatus, errorThrows) => {
                setSaving(false);
                console.error(
                    "Error adding bike counter",
                    jqXHR,
                    textStatus,
                    errorThrows
                );
                setSaveError(true);
            });
    }

    const handleAddRow = () => {
        setSchedules((prev) => {
            const newSchedule = { id: "new_" + new Date().toISOString(), startDate: null, endDate: null };
            return [...prev, newSchedule];
        });
    }

    return (

        <Dialog open={rowToEdit !== null} onClose={() => handleDialogClose()}>
            <Box sx={{ width: "444px", padding: "16px 24px 24px 24px" }}>
                <DialogTitle sx={{ padding: 0 }} id="form-dialog-title">
                    Edit Schedule
                </DialogTitle>

            <DialogContent sx={{padding: 0}}>
                <Box
                    sx={{
                        display: "flex",
                        flexDirection: "column",
                        gap: "16px",
                        marginTop: "16px",
                    }}
                >
                    {saveError && (
                        <Alert severity={"error"}>
                            Unable to save. Please try again later.
                        </Alert>
                    )}

                    <TextField
                        label={"Name"}
                        error={nameError}
                        helperText={nameError}
                        onChange={(e) => {
                            const lowerCasedNewVal = e.target.value.toLowerCase();
                            setName(e.target.value);
                            if (existingRows.some(row => row.name.toLowerCase() === lowerCasedNewVal && lowerCasedNewVal !== rowToEdit.name.toLowerCase())) {
                                setNameError("Name already exists");
                            }  else if (lowerCasedNewVal === "always" || lowerCasedNewVal === "never") {
                                setNameError("Name cannot be 'always' or 'never'");
                            }  else if (name.length > 100) {
                                setNameError("Name cannot be longer than 100 characters");
                            }  else {
                                setNameError(false);
                            }
                        }}
                        autoFocus
                        value={name}
                    />


                    {schedules.map((schedule) => {
                        return (
                            <Box key={schedule.id} sx={{ display: "flex", gap: "16px" }}>
                                <LocalizationProvider dateAdapter={AdapterDateFns}>
                                    <DatePicker label="Start Date"
                                                value={schedule.startDate ? new Date(schedule.startDate) : null}
                                                onChange={(nv) => {
                                                    startDateOnChange(schedule, nv);
                                                }}
                                                maxDate={schedule.endDate ? new Date(schedule.endDate) : null}
                                                slotProps={{
                                                    textField: {
                                                        helperText: dateError.get(schedule.id + "_start") || "",
                                                    }
                                                }}
                                                format={"yyyy-MM-dd"}
                                    />
                                </LocalizationProvider>
                                <LocalizationProvider dateAdapter={AdapterDateFns}>
                                    <DatePicker label="End Date"
                                                value={schedule.endDate ? new Date(schedule.endDate) : null}
                                                onChange={(nv) => {
                                                    endDateOnChange(schedule, nv);
                                                }}
                                                minDate={schedule.startDate ? new Date(schedule.startDate) : null}
                                                slotProps={{
                                                    textField: {
                                                        helperText: dateError.get(schedule.id + "_end") || "",
                                                    }
                                                }}
                                                format={"yyyy-MM-dd"}
                                    />
                                </LocalizationProvider>
                                <Box sx={{display: "flex", alignItems: "center"}}>
                                    <Button
                                        sx={{display: "flex", alignItems: "center", width: "40px", height: "40px"}}
                                        disabled={!schedule.startDate && !schedule.endDate}
                                        onClick={() => {
                                            if (typeof schedule.id === 'number') {
                                                setDeletedRanges((prev) => [...prev, schedule.id]);
                                                setEditRangeIds((prev) => {
                                                    if (prev.has(schedule.id)) {
                                                        const newSet = new Set(prev);
                                                        newSet.delete(schedule.id);
                                                        return newSet;
                                                    } else {
                                                        return prev;
                                                    }
                                                });
                                                setSchedules((prev) => {
                                                    return prev.map(s => s.id === schedule.id ? {...s, startDate: null, endDate: null} : s);
                                                });
                                                setDateError((prev) => {
                                                    const newError = new Map(prev);
                                                    newError.delete(schedule.id + "_start");
                                                    newError.delete(schedule.id + "_end");
                                                    return newError;
                                                });
                                                schedule.id = "new_" + new Date().toISOString();
                                                return;
                                            }
                                            setSchedules((prev) => {
                                                return prev.map(s => s.id === schedule.id ? {...s, startDate: null, endDate: null} : s);
                                            });
                                            setDateError((prev) => {
                                                const newError = new Map(prev);
                                                newError.delete(schedule.id + "_start");
                                                newError.delete(schedule.id + "_end");
                                                return newError;
                                            });
                                        }}
                                    >
                                        <ClearScheduleDates active={schedule.startDate || schedule.endDate} />
                                    </Button>
                                </Box>
                            </Box>
                        )
                    })}

                    {schedules.filter((s) => s.startDate !== null).length >= 3 ? (
                        <Box>
                            <Button onClick={() => handleAddRow()}><img src={plusSign} alt={"Add Row"} />ADD ROW</Button>
                        </Box>) : null}

                </Box>
            </DialogContent>


            <DialogActions sx={{ padding: 0, marginTop: "16px" }}>
                <Button
                    disabled={saving}
                    onClick={() => {
                        handleDialogClose();
                    }}
                >
                    Cancel
                </Button>
                <Button
                    onClick={handleSave}
                    disabled={
                        saving || name === "" || nameError || schedules.filter((s) => s.startDate !== null).length === 0 || dateError.size > 0
                    }
                    variant={"contained"}
                    sx={{
                        backgroundColor: "rgba(19, 151, 203, 1)",
                        gap: "8px",
                        width: "91.75px",
                        height: "36.5px",
                    }}
                >
                    {saving ? (<CircularProgress size={"24.5px"} />) : (
                        <>
                            <CheckIcon sx={{ width: "16px", height: "24px" }} />
                            Save
                        </>
                    )}
                </Button>
            </DialogActions>
            </Box>
        </Dialog>
    )

}
